import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_PUBLIC_KEY,
	STORAGE_WALLET_TYPE
	// NETWORK
} from '@/utils/constants'
import SafeLocalStorage from '../safe-local-storage'
import * as btc from '@scure/btc-signer'
import { allInputIndexes, extractFinalizedPsbtHex, finalizeTaprootKeyPath, inputScriptHex, parsePsbtBytes, parsePsbtTransaction, stripBip32Derivation, toPsbtHex } from './psbt'
import Wallet from './'
import { WalletProvider } from '@/utils/types/wallet'

import { isUserRejection } from './signing-errors'

export default class NintondoWallet implements WalletProvider {
	get name(): string {
		return 'Nintondo Wallet'
	}

	get capabilities(): string[] {
		return []
	}

	get connectIcon(): string {
		return '/connect-nintondo.png'
	}

	get enabled(): boolean {
		// return NETWORK === 'BELLS'
		return false
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.nintondo) {
			window.open('https://nintondo.io/', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async dummyPsbt(inscription_id: string): Promise<string> {
		const psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		return psbt
	}

	async login(): Promise<void> {
		// @ts-ignore
		if (!window.nintondo) {
			throw new Error('Nintondo is not installed. Install the extension, then reconnect.')
		}

		const { address, public_key } = await (window as any).nintondo
			.connect()
			.then(async (address: string) => ({
				address,
				public_key: await (window as any).nintondo.getPublicKey()
			}))

		localStorage.setItem(STORAGE_TAPROOT_ADDRESS, address)
		localStorage.setItem(STORAGE_WALLET_TYPE, 'nintondo')
		localStorage.setItem(STORAGE_PUBLIC_KEY, public_key)
		try {
			await Wallet.fetchProfile()
		} catch (_e) {}
	}

	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	taprootPublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	async signPSBT(psbt: string): Promise<string> {
		try {
			const bip371 = await import('bitcoinjs-lib/src/psbt/bip371')
			const { script } = btc.p2tr(
				bip371.toXOnly(Buffer.from(this.taprootPublicKey(), 'hex'))
			)
			const scriptHex = Buffer.from(script).toString('hex')

			const psbt_tx = parsePsbtTransaction(psbt)
			stripBip32Derivation(psbt_tx, { outputs: true })

			// Only the taproot account is ours to sign; never claim a payment input.
			const inputsToSign: Array<{ address: string; signingIndexes: number[]; sigHash: number }> = []
			for (const i of allInputIndexes(psbt_tx)) {
				if (inputScriptHex(psbt_tx, i) === scriptHex) {
					inputsToSign.push({
						address: this.taprootAddress(),
						signingIndexes: [i],
						sigHash: psbt_tx.getInput(i).sighashType || 1
					})
				}
			}

			const psbtBytes = Buffer.from(psbt_tx.toPSBT())
			// @ts-ignore
			const data = await window.nintondo.signTx(psbtBytes)
			const tx = parsePsbtBytes(data)
			finalizeTaprootKeyPath(
				tx,
				inputsToSign.map((e) => e.signingIndexes[0])
			)
			return toPsbtHex(tx)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await this.signPSBT(psbt)
			return extractFinalizedPsbtHex(data)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signMessage(message: String): Promise<string> {
		try {
			// @ts-ignore
			const res = await window.nintondo.signMessage(message, 'bip322-simple')
			return res
		} catch (_e) {
			return undefined as unknown as string
		}
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signTransfer(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signEscrow(psbt: string): Promise<string> {
		return this.signPSBT(psbt)
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		try {
			let signedSetup = ''

			if (setup_psbt) {
				// @ts-ignore
				const signedSetupPSBT = await this.signPSBT(setup_psbt)
				signedSetup = extractFinalizedPsbtHex(signedSetupPSBT)
			}
			// @ts-ignore
			const signedPurchase = await this.signPSBT(purchase_psbt)
			return { signedSetup, signedPurchase }
		} catch (e) {
			if (isUserRejection(e)) {
				return undefined as unknown as { signedSetup: string; signedPurchase: string }
			}
			console.log(e)
			throw e
		}
	}
}
